import { useEffect, useRef } from "react";

/**
 * A hook that returns a debounced version of the provided callback function.
 *
 * @template T - The type of the callback parameter.
 * @param callback - The function to debounce.
 * @param delay - The debounce delay in milliseconds.
 * @returns A debounced version of the callback function.
 */
export function useDebounce<T>(
  callback: (arg: T) => void,
  delay: number
): (arg: T) => void {
  const timeoutRef = useRef<number | null>(null);

  const debouncedFunction = (arg: T) => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      callback(arg);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFunction;
}
